"use client"

import {
  LogIn,
  Settings,
} from "lucide-react"
import { Link } from "react-router-dom"

import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import { useAuth } from "@workos-inc/authkit-react"
import { useConvexAuth, useQuery } from "convex/react"
import { useState } from "react"
import { useHidePersonalData } from "@/hooks/use-hide-personal-data"
import { Avatar, AvatarImage, AvatarFallback } from "./ui/avatar"
import { api } from "../../convex/_generated/api"


export function NavUser() {
  const { user, signIn } = useAuth()
  const { isLoading: authLoading, isAuthenticated } = useConvexAuth()
  const userRecord = useQuery(api.users.getCurrentUserDetails)
  const [isLoading, setIsLoading] = useState(false)
  const [hidePersonalData] = useHidePersonalData()

  // Show loading state while user data is being fetched
  if (authLoading) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" disabled>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium">Loading...</span>
              <span className="truncate text-xs">Please wait</span>
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    )
  }

  // Guests: customization settings + login (no account/billing)
  if (!isAuthenticated) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" asChild>
            <Link to="/settings" className="flex items-center gap-3">
              <Settings />
              <span>Settings</span>
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
        <SidebarMenuItem>
          <SidebarMenuButton
            size="lg"
            onClick={() => {
              setIsLoading(true);
              signIn();
            }}
            disabled={isLoading}
          >
            <LogIn />
            <span>{isLoading ? "Loading..." : "Login"}</span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    )
  }

  const nameParts = [
    userRecord?.firstName ?? user?.firstName,
    userRecord?.lastName ?? user?.lastName,
  ].filter(Boolean)
  const userName = nameParts.length > 0 ? nameParts.join(" ") : undefined
  const userEmail =
    userRecord?.email ?? (user && "email" in user ? user.email : undefined)
  const fallbackName =
    userName ||
    (userEmail ? userEmail.split("@")[0] : undefined) ||
    "User";
  const initials = fallbackName
    .split(" ")
    .filter(Boolean)
    .map((part: string) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton size="lg" asChild>
          <Link to="/settings" className="flex items-center gap-3">
            {hidePersonalData ? (
              <>
                <div className="size-8 shrink-0 rounded-full bg-muted" aria-hidden />
                <div className="grid flex-1 min-w-0 gap-0.5">
                  <div className="h-4 w-24 rounded bg-muted" aria-hidden />
                  <div className="h-3.5 w-32 rounded bg-muted" aria-hidden />
                </div>
              </>
            ) : (
              <>
                <Avatar className="size-8 rounded-lg">
                  <AvatarImage
                    src={userRecord?.profilePictureURL || user?.profilePictureUrl || ""}
                    alt={fallbackName}
                  />
                  <AvatarFallback className="rounded-lg">
                    {initials || "U"}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight min-w-0">
                  <span className="truncate font-medium">{fallbackName}</span>
                  <span className="truncate text-xs text-muted-foreground">{userEmail || "No email"}</span>
                </div>
              </>
            )}
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}

